import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo } from 'react';
import { sortBy } from 'lodash';
import { AssociatedCompany } from '../../types';
import {
  StyledHeaderContainer,
  StyledShipToItem,
  StyledShipToListContainer,
  StyledShipToListTitle,
  drawerPaper,
} from './CustomerShipToDrawer.styles';

type CustomerShipToDrawerProp = {
  customer: AssociatedCompany | null;
  onClose: () => void;
};

export const CustomerShipToDrawer = ({
  customer,
  onClose,
}: CustomerShipToDrawerProp) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  const shipTos = useMemo(
    () => sortBy(customer?.customerAddresses ?? [], 'id'),
    [customer?.customerAddresses]
  );

  return (
    <Drawer
      transitionDuration={100}
      open={!!customer}
      PaperProps={{ sx: drawerPaper(isMobile) }}
      anchor="right"
    >
      <StyledHeaderContainer display="flex" justifyContent="space-between">
        <Grid
          container
          alignItems="center"
          display="flex"
          gap={3}
          wrap="nowrap"
          mr={3}
        >
          <Typography variant="h3" color={theme.palette.common.white}>
            {customer?.companyName}
          </Typography>
        </Grid>
        <IconButton onClick={onClose}>
          <CloseIcon htmlColor={theme.palette.common.white} />
        </IconButton>
      </StyledHeaderContainer>

      <StyledShipToListContainer>
        {shipTos.length === 0 ? (
          <Box px={3} alignSelf="center">
            <Typography fontWeight="700" variant="h4">
              {t('agencies.fields.emptyShipTo')}
            </Typography>
          </Box>
        ) : (
          <Stack>
            <StyledShipToListTitle>
              <Typography fontWeight="700" variant="h4">
                {t('agencies.fields.shipToList')}
              </Typography>
            </StyledShipToListTitle>
            <Box>
              {shipTos.map((shipTo) => (
                <StyledShipToItem key={shipTo.id}>
                  <Typography
                    fontWeight="500"
                    variant="body1"
                  >{`${shipTo.id} - ${shipTo.name}`}</Typography>
                </StyledShipToItem>
              ))}
            </Box>
          </Stack>
        )}
      </StyledShipToListContainer>
    </Drawer>
  );
};
